import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, type SubmitErrorHandler, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import SearchableSelect from "../../../components/SearchableSelect";
import { Button } from "../../../components/ui/button";
import { Field, FieldError, FieldLabel } from "../../../components/ui/field";
import { Input } from "../../../components/ui/input";
import { months, monthToNumber } from "../../../handlers/utilityFunctions";
import { getJobFunctions } from "../../../seed/getMetadata";
import { useUserAccount } from "../../../services/create-resume/queries";
import { ResumeDataService } from "../../../services/create-resume/resume-data";
import OptionBadge from "../components/OptionBadge";
import AuthLayout from "../layout/AuthLayout";
import {
	clearPendingSignup,
	getPendingSignup,
	type PendingSignup,
	setPendingSignup,
	type YesNo,
} from "../pendingSignup";
import { deriveSeniorityLevel } from "../seniority";

const MAX_JOB_TITLE_LENGTH = 100;

const onboardingSchema = z
	.object({
		firstName: z.string().trim().min(1, "Please enter your first and last name"),
		lastName: z.string().trim().min(1, "Please enter your first and last name"),
		jobTitle: z.string().trim().min(1, "Please enter your desired job title"),
		areasOfExpertise: z.array(z.string()).min(1, "Please select your area of expertise"),
		recentGraduate: z
			.string()
			.refine((value) => value === "Yes" || value === "No", "Please let us know if you are a recent graduate"),
		experienceMonth: z.string(),
		experienceYear: z.string(),
	})
	.superRefine((values, context) => {
		if (values.experienceMonth && values.experienceYear) return;

		context.addIssue({
			code: "custom",
			path: ["experienceMonth"],
			message:
				values.recentGraduate === "Yes"
					? "Please select when you graduated or expect to graduate"
					: "Please select when you started your professional career",
		});
	});

type OnboardingFormInput = z.input<typeof onboardingSchema>;
type OnboardingFormValues = z.output<typeof onboardingSchema>;

/** Graduating soon counts too, so the window runs a couple of years into the future. */
function graduationYears(): string[] {
	const thisYear = new Date().getFullYear();
	const years = [];
	for (let year = thisYear - 3; year <= thisYear + 2; year++) years.push(String(year));
	return years.reverse();
}

function careerStartYears(): string[] {
	const thisYear = new Date().getFullYear();
	const years = [];
	for (let year = 1980; year <= thisYear; year++) years.push(String(year));
	return years.reverse();
}

function OnboardingScreen() {
	const navigate = useNavigate();
	const pendingSignup = useMemo(() => getPendingSignup(), []);
	const api = useMemo(() => new ResumeDataService(), []);
	const jobFunctionOptions = useMemo(() => [...getJobFunctions()].sort(), []);
	const queryClient = useQueryClient();
	const {
		clearErrors,
		control,
		formState: { errors },
		handleSubmit: submitForm,
		register,
		reset,
		setValue,
		watch,
	} = useForm<OnboardingFormInput, unknown, OnboardingFormValues>({
		resolver: zodResolver(onboardingSchema),
		defaultValues: {
			firstName: pendingSignup?.firstName || "",
			lastName: pendingSignup?.lastName || "",
			jobTitle: pendingSignup?.jobTitle || "",
			areasOfExpertise: pendingSignup?.areasOfExpertise || [],
			recentGraduate: pendingSignup?.recentGraduate || "",
			experienceMonth: pendingSignup?.experienceMonth || "",
			experienceYear: pendingSignup?.experienceYear || "",
		},
	});
	const recentGraduate = watch("recentGraduate");
	const [submitting, setSubmitting] = useState(false);
	// Synchronous guard: `submitting` state only disables the button on the next
	// render, so a fast double-submit could fire two POSTs before that lands.
	const isCreatingRef = useRef(false);

	// A draft rebuilt at sign-in for an account whose record went missing carries no name — Cognito
	// only ever stores the email address. POST /users requires both, so ask for them here rather than
	// sending the user through a second screen. Derived from the stored draft, not from state, so the
	// fields don't vanish while they're being filled in.
	const needsName = Boolean(pendingSignup) && !(pendingSignup.firstName?.trim() && pendingSignup.lastName?.trim());

	const answeredQuestions = useRef<Set<string>>(new Set());

	// If a signed-in user reaches this page with a stale signup draft, the presence of a backend
	// record is enough to prove that account setup already completed. New accounts do not receive
	// that record until onboarding is submitted.
	const { data: userAccount, isLoading: isLoadingAccount } = useUserAccount();
	const hasUserRecord = Boolean(userAccount?.userID);

	function trackQuestionAnswered(question: string) {
		if (answeredQuestions.current.has(question)) return;
		answeredQuestions.current.add(question);
		posthog.capture("registration_question_answered", { question, step: "onboarding" });
	}

	useEffect(() => {
		if (!pendingSignup) return;
		posthog.capture("registration_onboarding_started", { method: pendingSignup.method });
	}, [pendingSignup]);

	// Rehydrate if the user came back to this step.
	useEffect(() => {
		if (!pendingSignup) return;
		reset({
			firstName: pendingSignup.firstName || "",
			lastName: pendingSignup.lastName || "",
			jobTitle: pendingSignup.jobTitle || "",
			areasOfExpertise: pendingSignup.areasOfExpertise || [],
			recentGraduate: pendingSignup.recentGraduate || "",
			experienceMonth: pendingSignup.experienceMonth || "",
			experienceYear: pendingSignup.experienceYear || "",
		});
	}, [pendingSignup, reset]);

	// Don't flash the questions at someone who is about to be bounced past them.
	if (isLoadingAccount) {
		return null;
	}

	if (hasUserRecord) {
		// The register form wrote a draft on the way in; it would otherwise re-POST /users later.
		clearPendingSignup();
		return <Navigate to="/profile" replace />;
	}

	if (!pendingSignup) {
		return <Navigate to="/register" replace />;
	}

	// Create the backend record from the collected draft and land on the profile.
	// Used for accounts that are already signed in (Google, or an email account
	// whose link was already followed), so the record exists before they reach a
	// page that requires it.
	function createAccountAndGoToProfile(draft: PendingSignup) {
		if (isCreatingRef.current) return;
		isCreatingRef.current = true;
		setSubmitting(true);

		// Persist the answers before the request so they survive a refresh — or the
		// response interceptor redirecting on an auth failure — and are cleared only
		// once the record exists.
		setPendingSignup(draft);

		const experienceDate =
			draft.experienceMonth && draft.experienceYear
				? { month: monthToNumber(draft.experienceMonth), year: Number(draft.experienceYear) }
				: null;
		const experienceKey = draft.recentGraduate === "Yes" ? "graduationDate" : "professionalSince";

		// Only the keys the backend whitelists — it rejects unknown fields.
		const data = {
			emailAddress: draft.email,
			firstName: draft.firstName,
			lastName: draft.lastName,
			...(draft.phone ? { phoneNumber: draft.phone } : {}),
			jobPreferences: {
				desiredTitle: draft.jobTitle,
				areasOfExpertise: draft.areasOfExpertise || [],
				seniorityLevel: draft.seniorityLevel,
				...(draft.recentGraduate ? { recentGraduate: draft.recentGraduate } : {}),
				...(draft.recentGraduate && experienceDate ? { [experienceKey]: experienceDate } : {}),
			},
		};

		api.createUserAccount(
			data,
			(response: { data: unknown }) => {
				localStorage.setItem("jobPreferences", JSON.stringify(response.data));
				clearPendingSignup();
				// A stale anonymous account read must not outlive the sign-in that just happened.
				queryClient.invalidateQueries({ queryKey: ["user", "account"] });
				navigate("/profile");
			},
			(error: { response?: { data?: { message?: string | string[] } } }) => {
				isCreatingRef.current = false;
				setSubmitting(false);
				// class-validator returns `message` as a string[] for validation failures.
				const message = error?.response?.data?.message;
				toast.error(
					(Array.isArray(message) ? message.join(". ") : message) ||
						"We couldn't finish setting up your profile. Please try again.",
				);
			},
		);
	}

	function onValid({
		firstName,
		lastName,
		jobTitle,
		areasOfExpertise,
		recentGraduate,
		experienceMonth,
		experienceYear,
	}: OnboardingFormValues) {
		// A second submit that lands before the button re-renders as disabled would otherwise
		// re-run the validation and the "completed" analytics event.
		if (isCreatingRef.current) return;
		if (recentGraduate !== "Yes" && recentGraduate !== "No") return;

		const seniorityLevel = deriveSeniorityLevel(monthToNumber(experienceMonth), Number(experienceYear));

		const draft: PendingSignup = {
			...pendingSignup,
			firstName,
			lastName,
			jobTitle,
			areasOfExpertise,
			recentGraduate,
			experienceMonth,
			experienceYear,
			seniorityLevel,
		};

		posthog.capture("registration_onboarding_completed", {
			method: draft.method,
			recent_graduate: recentGraduate,
			// The band isn't asked for any more, so record what we inferred.
			derived_seniority_level: seniorityLevel,
		});

		// An email account is not signed in until its verification link is followed, so it finishes
		// through /creating-profile, which creates the record and shows the "check your email" screen.
		// A Google (or already-verified) account is signed in now: create the record here and go
		// straight to the profile, so there is no step where a signed-in user has no record yet.
		if (draft.method === "google" || draft.emailVerified) {
			createAccountAndGoToProfile(draft);
		} else {
			setPendingSignup(draft);
			navigate("/creating-profile", { state: { method: draft.method } });
		}
	}

	const onInvalid: SubmitErrorHandler<OnboardingFormInput> = (invalidErrors) => {
		if (isCreatingRef.current) return;

		const validationMessage = [
			invalidErrors.firstName?.message,
			invalidErrors.lastName?.message,
			invalidErrors.jobTitle?.message,
			invalidErrors.areasOfExpertise?.message,
			invalidErrors.recentGraduate?.message,
			invalidErrors.experienceMonth?.message,
		].find((message): message is string => typeof message === "string");

		if (validationMessage) toast.error(validationMessage);
	};

	return (
		<AuthLayout>
			<div className="py-12">
				<div className="mb-6 flex flex-col items-center space-y-2">
					<h3 className="font-bold! text-[28px] leading-tight text-[#161616] font-plus-jakarta! mb-2 text-center">
						Welcome{pendingSignup.firstName ? `, ${pendingSignup.firstName}` : ""}!
					</h3>
					<p className="text-[#5C5C5C] text-sm text-center">
						{needsName
							? "Let's finish setting up your account so we can start matching you with the right jobs."
							: "Three quick questions so we can start matching you with the right jobs."}
					</p>
				</div>

				<form noValidate onSubmit={submitForm(onValid, onInvalid)} className="flex flex-col gap-4">
					{needsName ? (
						<div className="grid grid-cols-2 gap-3 mb-4">
							<Field className="gap-1" data-invalid={!!errors.firstName}>
								<FieldLabel htmlFor="onboarding-first-name">First name</FieldLabel>
								<Input
									id="onboarding-first-name"
									type="text"
									placeholder="Enter your first name"
									aria-invalid={!!errors.firstName}
									aria-describedby={errors.firstName ? "onboarding-first-name-error" : undefined}
									{...register("firstName", {
										onBlur: (event) => {
											if (event.target.value.trim()) trackQuestionAnswered("first_name");
										},
									})}
								/>
								<FieldError id="onboarding-first-name-error" errors={[errors.firstName]} />
							</Field>
							<Field className="gap-1" data-invalid={!!errors.lastName}>
								<FieldLabel htmlFor="onboarding-last-name">Last name</FieldLabel>
								<Input
									id="onboarding-last-name"
									type="text"
									placeholder="Enter your last name"
									aria-invalid={!!errors.lastName}
									aria-describedby={errors.lastName ? "onboarding-last-name-error" : undefined}
									{...register("lastName", {
										onBlur: (event) => {
											if (event.target.value.trim()) trackQuestionAnswered("last_name");
										},
									})}
								/>
								<FieldError id="onboarding-last-name-error" errors={[errors.lastName]} />
							</Field>
						</div>
					) : null}

					<Field className="gap-1 mb-4" data-invalid={!!errors.jobTitle}>
						<FieldLabel htmlFor="onboarding-job-title">What&#39;s your dream job title?</FieldLabel>
						<Input
							id="onboarding-job-title"
							type="text"
							placeholder="Enter your desired job title"
							maxLength={MAX_JOB_TITLE_LENGTH}
							aria-invalid={!!errors.jobTitle}
							aria-describedby={errors.jobTitle ? "onboarding-job-title-error" : undefined}
							{...register("jobTitle", {
								onBlur: (event) => {
									if (event.target.value.trim()) trackQuestionAnswered("job_title");
								},
							})}
						/>
						<FieldError id="onboarding-job-title-error" errors={[errors.jobTitle]} />
					</Field>

					<Controller
						name="areasOfExpertise"
						control={control}
						render={({ field }) => (
							<Field className="gap-1 mb-4" data-invalid={!!errors.areasOfExpertise}>
								<FieldLabel htmlFor="onboarding-areas-of-expertise">
									What&#39;s your superpower skill or area of expertise?
								</FieldLabel>
								<SearchableSelect
									id="onboarding-areas-of-expertise"
									name={field.name}
									placeholder="Select all that apply"
									multiple
									search
									options={jobFunctionOptions}
									value={field.value}
									onChange={(selectedOption) => {
										if (field.value.includes(selectedOption)) {
											field.onChange(field.value.filter((item) => item !== selectedOption));
										} else {
											field.onChange([...field.value, selectedOption]);
											trackQuestionAnswered("area_of_expertise");
										}
									}}
									onBlur={field.onBlur}
									aria-invalid={!!errors.areasOfExpertise}
									aria-describedby={errors.areasOfExpertise ? "onboarding-areas-of-expertise-error" : undefined}
								/>
								<FieldError id="onboarding-areas-of-expertise-error" errors={[errors.areasOfExpertise]} />
							</Field>
						)}
					/>

					<Controller
						name="recentGraduate"
						control={control}
						render={({ field }) => (
							<Field className="gap-1 mb-6" data-invalid={!!errors.recentGraduate}>
								<FieldLabel id="onboarding-recent-graduate-label">Are you a recent graduate?</FieldLabel>
								<div
									className="flex gap-3 mt-1 flex-wrap"
									role="group"
									aria-labelledby="onboarding-recent-graduate-label"
									aria-invalid={!!errors.recentGraduate}
									aria-describedby={errors.recentGraduate ? "onboarding-recent-graduate-error" : undefined}
								>
									{(["Yes", "No"] as YesNo[]).map((answer) => (
										<OptionBadge
											key={answer}
											value={answer}
											selected={field.value === answer}
											onSelect={() => {
												if (answer !== field.value) {
													// The two branches ask about different dates — don't carry one answer into the other.
													setValue("experienceMonth", "");
													setValue("experienceYear", "");
													clearErrors(["experienceMonth", "experienceYear"]);
												}
												field.onChange(answer);
												trackQuestionAnswered("recent_graduate");
											}}
										/>
									))}
								</div>
								<FieldError id="onboarding-recent-graduate-error" errors={[errors.recentGraduate]} />

								{recentGraduate ? (
									<Field className="gap-1 mt-3" data-invalid={!!errors.experienceMonth}>
										<FieldLabel id="onboarding-experience-date-label">
											{recentGraduate === "Yes"
												? "When did you graduate or expect to graduate?"
												: "When did you start your professional career?"}
										</FieldLabel>
										<div className="grid grid-cols-2 gap-3">
											<Controller
												name="experienceMonth"
												control={control}
												render={({ field: monthField }) => (
													<SearchableSelect
														id="onboarding-experience-month"
														name={monthField.name}
														placeholder="Select Month"
														search
														options={months}
														value={monthField.value}
														onChange={(selectedMonth) => {
															monthField.onChange(selectedMonth);
															trackQuestionAnswered(recentGraduate === "Yes" ? "graduation_date" : "career_start");
														}}
														onBlur={monthField.onBlur}
														aria-invalid={!!errors.experienceMonth}
														aria-describedby={errors.experienceMonth ? "onboarding-experience-date-error" : undefined}
														aria-labelledby="onboarding-experience-date-label"
													/>
												)}
											/>
											<Controller
												name="experienceYear"
												control={control}
												render={({ field: yearField }) => (
													<SearchableSelect
														id="onboarding-experience-year"
														name={yearField.name}
														placeholder="Select Year"
														search
														options={recentGraduate === "Yes" ? graduationYears() : careerStartYears()}
														value={yearField.value}
														onChange={(selectedYear) => {
															yearField.onChange(selectedYear);
															trackQuestionAnswered(recentGraduate === "Yes" ? "graduation_date" : "career_start");
														}}
														onBlur={yearField.onBlur}
														aria-invalid={!!errors.experienceMonth}
														aria-describedby={errors.experienceMonth ? "onboarding-experience-date-error" : undefined}
														aria-labelledby="onboarding-experience-date-label"
													/>
												)}
											/>
										</div>
										<FieldError id="onboarding-experience-date-error" errors={[errors.experienceMonth]} />
									</Field>
								) : null}
							</Field>
						)}
					/>

					<Button className="w-full" type="submit" disabled={submitting}>
						{submitting ? "Setting up your profile…" : "Continue"}
					</Button>
				</form>
			</div>
		</AuthLayout>
	);
}

export default OnboardingScreen;
